import React, { PureComponent } from "react";
import { connect } from "react-redux";
import empty from "is-empty";
import { ModalActions } from "src/redux/Modal/actions";
import { MdActions } from "src/redux/MarketData/actions";
import { InventoryActions } from "src/redux/Inventory/actions";
import { ConfirmActions } from "src/redux/Confirm/actions";
import styles from "./styles.module.scss";

/// Keep this low.
const SearchInProgressPopupTimeout = 40;

class SearchSecurity extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            searchTerm: "",
            typingTimeout: null,
            searchResults: [],
            noMatches: false,
            isSearching: false,
            searchingTimeout: null,
        };
    }

    componentDidMount() {
        if (this.inputRef) {
            this.inputRef.focus();
        }

        document.addEventListener("keydown", this.handleEscPress);
    }

    handleEscPress = (event) => {
        if (event.key === "Escape") {
            this.closeDialog();
        }
    };

    closeDialog = () => {
        const { close } = this.props;
        if (close) {
            close();
        }
    };

    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleEscPress);
        if (this.state.searchingTimeout) {
            clearTimeout(this.state.searchingTimeout);
        }
    }

    // Debounced API call
    searchSecurity = async (term) => {
        if (empty(term) || term.length < 2) {
            this.setState({
                searchResults: [],
                noMatches: false,
                isSearching: false,
            });
            return;
        }

        if (this.state.searchingTimeout) {
            clearTimeout(this.state.searchingTimeout);
        }

        const timeout = setTimeout(() => {
            this.setState({ isSearching: true });
        }, SearchInProgressPopupTimeout);

        this.setState({ searchingTimeout: timeout });

        try {
            const result = await this.props.dispatch(
                MdActions.SearchSecurities(term)
            );
            const stocks = result?.stocks || [];

            // Tricky business: The user may want to block multiple stocks with similar names,
            // So searching twice with the same prefix should filter out the stocks already
            // in the current or blocked pending list.
            // Tricky because we should ideally do this filtering on the backend,
            // otherwise we risk having our entire paged response blanked out,
            // which is misleading
            // Note: this should be a very rare use-case. Fix only if necessary.
            const bbgCodes = this.props.currentAndPendingSecurityBbgTickers;
            const filteredStocks = stocks.filter(
                (stock) => !bbgCodes.has(stock.bbgCode)
            );

            // Response arrived - Clear out "Searching..." popup
            clearTimeout(timeout);

            this.setState({
                searchResults: filteredStocks,
                noMatches: filteredStocks.length === 0,
                isSearching: false,
                searchingTimeout: null,
            });
        } catch (err) {
            console.error("Search failed:", err);
            clearTimeout(timeout);

            this.setState({
                searchResults: [
                    {
                        id: "__error__",
                        name: "Search failed. Please try again later, or report error to TRU support team if the error persists.",
                        bbgCode: "",
                        isin: "",
                        isError: true, // we'll use this flag in rendering
                    },
                ],
                noMatches: true,
                isSearching: false,
                searchingTimeout: null,
            });
        }
    };

    handleInputChange = (event) => {
        const { value } = event.target;

        this.setState((prevState) => {
            if (prevState.typingTimeout) {
                clearTimeout(prevState.typingTimeout);
            }

            return {
                searchTerm: value,
                noMatches: false, // Clear "No matches found" when user starts typing
                typingTimeout: setTimeout(
                    () => this.searchSecurity(value),
                    200
                ),
            };
        });
    };

    handleSelection = (stock) => {
        this.props.onSelect(stock.id);
    };

    render() {
        const { searchTerm, searchResults, noMatches, isSearching } =
            this.state;

        return (
            <div className={styles.inputBox}>
                <div style={{ position: "relative" }}>
                    <input
                        ref={(input) => {
                            this.inputRef = input;
                        }}
                        type="text"
                        value={searchTerm}
                        onChange={this.handleInputChange}
                        placeholder="Search by Security Name, ISIN or Ticker"
                    />

                    {searchTerm.length >= 2 &&
                        (searchResults.length > 0 ||
                            noMatches ||
                            isSearching) && (
                            <div className={styles.resultsDropdown}>
                                {searchResults.length > 0 ? (
                                    <>
                                        {searchResults
                                            .slice(0, 5)
                                            .map((stock) => {
                                                if (stock.isError) {
                                                    return (
                                                        <div
                                                            key="error"
                                                            className={
                                                                styles.dropdownItem
                                                            }
                                                            style={{
                                                                pointerEvents:
                                                                    "none",
                                                            }}
                                                        >
                                                            {stock.name}
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <div
                                                        key={stock.id}
                                                        className={
                                                            styles.dropdownItem
                                                        }
                                                        onClick={() =>
                                                            this.handleSelection(
                                                                stock
                                                            )
                                                        }
                                                    >
                                                        <strong>
                                                            {stock.name}
                                                        </strong>{" "}
                                                        / {stock.bbgCode} /{" "}
                                                        {stock.isin}
                                                    </div>
                                                );
                                            })}

                                        {searchResults.length > 5 && (
                                            <div
                                                className={styles.dropdownItem}
                                            >
                                                <span
                                                    className={
                                                        styles.dropdownEllipsis
                                                    }
                                                >
                                                    ...keep typing to narrow
                                                    down results
                                                </span>
                                            </div>
                                        )}
                                    </>
                                ) : isSearching ? (
                                    <div className={styles.dropdownItem}>
                                        Searching...
                                    </div>
                                ) : (
                                    <div className={styles.dropdownItem}>
                                        No matches found
                                    </div>
                                )}
                            </div>
                        )}
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({
    dispatch,
    openModal: (component, options) =>
        dispatch(ModalActions.Open(component, options)),
    prepareInit: (rowData) =>
        dispatch(InventoryActions.SetUploadEntries(rowData)),
    openConfirm: (options) => dispatch(ConfirmActions.Open(options)),
    close: () => dispatch(ModalActions.Close()),
    closeAll: () => dispatch(ModalActions.CloseAll()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SearchSecurity);
